import { Badge } from '@/components/ui/shadcn/badge';
import { useTranslations } from '@/hooks/useTranslations';
import { trySettingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface PriorityBadgeProps {
    priority?: string | null;
    className?: string;
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    /**
     * `settings.*` group to resolve the label from, e.g. 'company_priorities'.
     *
     * The `label` inside a settings row is authored in English, so without this
     * it would win over an admin's per-language override. Omit to keep the
     * legacy behaviour (settings label first) for callers not yet migrated.
     */
    translationNamespace?: string;
}

// Map icon strings to React components
const getIconComponent = (iconString?: string): React.ReactNode => {
    if (!iconString) return null;

    // Map common icon strings to components
    const iconMap: Record<string, React.ReactNode> = {
        '↓': <CheckCircle2 className="h-3 w-3" />,
        '→': <AlertCircle className="h-3 w-3" />,
        '↑': <AlertTriangle className="h-3 w-3" />,
    };

    return iconMap[iconString] || <span className="text-xs">{iconString}</span>;
};

export function PriorityBadge({ priority, className, priorities, translationNamespace }: PriorityBadgeProps) {
    const { t } = useTranslations();
    if (!priority) return null;
    const priorityConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon?: React.ReactNode }> = {
        low: { label: t('work_orders.show.priority.low', 'Low'), variant: 'secondary', icon: <CheckCircle2 className="h-3 w-3" /> },
        medium: { label: t('work_orders.show.priority.medium', 'Medium'), variant: 'outline', icon: <AlertCircle className="h-3 w-3" /> },
        high: { label: t('work_orders.show.priority.high', 'High'), variant: 'destructive', icon: <AlertTriangle className="h-3 w-3" /> },
    };

    // Try to find priority in dynamic data
    const dynamicPriority = priorities?.find((p) => p.value === priority || p.value.toLowerCase() === priority.toLowerCase());

    if (dynamicPriority) {
        // Use dynamic data with color classes from settings
        const iconComponent = getIconComponent(dynamicPriority.icon);

        // A translation for the stored slug beats the settings row's own label,
        // which is always English.
        const translated = translationNamespace ? trySettingLabel(t, translationNamespace, dynamicPriority.value) : null;

        return (
            <Badge variant="outline" className={cn('flex items-center gap-1.5 capitalize', dynamicPriority.color, className)}>
                {iconComponent}
                {translated ?? dynamicPriority.label}
            </Badge>
        );
    }

    // Fall back to hardcoded config
    const config = priorityConfig[priority.toLowerCase()] || { label: priority, variant: 'outline' as const };

    return (
        <Badge variant={config.variant} className={cn('flex items-center gap-1.5 capitalize', className)}>
            {config.icon}
            {config.label}
        </Badge>
    );
}
